import React, { Fragment, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import _ from "lodash"
import moment from "moment"
import { useLocation } from "react-router-dom"
import { makeStyles } from "@material-ui/core/styles"
import {
	Box,
	Typography,
	Button,
	Card,
	CardContent,
	CardActions,
	CardHeader,
	Grid,
	List,
	ListItem,
	IconButton,
	ListItemAvatar,
	Avatar,
	ListItemText,
} from "@material-ui/core"
import { useTheme } from "@material-ui/core/styles"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import ExpandLessIcon from "@material-ui/icons/ExpandLess"
import AddComment from "./comment/AddComment"
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction"
import { getComments, deleteComment as deleteCommentById } from "../../../actions/documentsActions"
import ConfirmationDialog from "../../common/dialog/ConfirmationDialog"
import { Skeleton } from "@material-ui/lab"
import TrendingFlatIcon from "@material-ui/icons/TrendingFlat"
import ArrowForwardIcon from "@material-ui/icons/ArrowForward"
import { CommentSVG } from "../../../components/icons"
import CommentDialog from "../../../components/common/dialog/Simple"
import { getAuditHistory, getAuditHistoryFailed } from "../../../actions/viewerActions"
import { TAGGED_DATE_FORMAT } from "../../../utils"

const useStyles = makeStyles((theme) => ({
	commentDialog: {
		background: theme.palette.grey[50],
		"& .MuiListItemText-primary": {
			fontSize: theme.typography.fontSize,
		},
		"& .MuiListItem-root": {
			padding: 0,
		},
	},
	auditHistory: {
		maxHeight: "50vh",
		overflow: "auto",
	},
	commentContentTitle: {
		color: theme.palette.grey.D100,
		padding: "0 0 10px",
		fontStyle: "italic",
	},
}))

function AuditHistory(props) {
	const classes = useStyles()
	const dispatch = useDispatch()
	const theme = useTheme()
	const location = useLocation()
	const [comments, setComments] = useState([])
	const [open, setOpen] = useState(false)
	const [commentId, setCommentId] = useState("")
	const [editCommentData, setEditCommentData] = useState({})
	const [commentDialog, setCommentDialog] = useState(false)
	const [commentDialogData, setCommentDialogData] = useState([])
	const [t] = useTranslation("global")

	function stringToColor(string) {
		let hash = 0
		let i
		for (i = 0; i < string.length; i += 1) {
			hash = string.charCodeAt(i) + ((hash << 5) - hash)
		}
		let color = "#"
		for (i = 0; i < 3; i += 1) {
			const value = (hash >> (i * 8)) & 0xff
			color += `00${value.toString(16)}`.slice(-2)
		}
		return color
	}

	var getContrast = function (hexcolor) {
		if (hexcolor.slice(0, 1) === "#") {
			hexcolor = hexcolor.slice(1)
		}
		var r = parseInt(hexcolor.substr(0, 2), 16)
		var g = parseInt(hexcolor.substr(2, 2), 16)
		var b = parseInt(hexcolor.substr(4, 2), 16)
		var yiq = (r * 299 + g * 587 + b * 114) / 1000
		return yiq >= 128 ? theme.palette.text.primary : theme.palette.background.default
	}
	const { isAuditHistoryLoading, auditHistory } = useSelector((state) => state.viewerData)
	const { user, loggedIn } = useSelector((state) => state.authtentication)

	const getTime = (item) => {
		return `${moment(moment.utc(item?.timestamp)).local().format("LL")} at ${moment(moment.utc(item?.timestamp))
			.local()
			.format("h:mma")}`
	}

	const hexToRGB = (hex = theme.palette.background.paper, alpha) => {
		let r = parseInt(hex.slice(1, 3), 16),
			g = parseInt(hex.slice(3, 5), 16),
			b = parseInt(hex.slice(5, 7), 16)

		if (alpha) {
			return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")"
		} else {
			return "rgb(" + r + ", " + g + ", " + b + ")"
		}
	}

	const templateForListView = (action, primary, data) => {
		return (
			<List dense={true} style={{ padding: 0, margin: 0 }}>
				<ListItem>
					<ListItemAvatar>
						<Avatar alt={"A"} {...stringAvatar(data?.username)} />
					</ListItemAvatar>
					<ListItemText primary={primary} secondary={getTime(data)} />
					{["add_comment", "delete_comment", "update_comment"].includes(action) && (
						<ListItemSecondaryAction>
							<IconButton
								edge="end"
								aria-label="delete"
								style={{
									cursor: ["add_comment", "delete_comment"].includes(action) ? "default" : "pointer",
								}}
							>
								<Box
									onClick={() => {
										if (action === "update_comment") {
											setCommentDialog(true)
											setCommentDialogData(data)
										}
									}}
									style={{
										opacity: ["add_comment", "delete_comment"].includes(action) ? "0.4" : 1,
										cursor: ["add_comment", "delete_comment"].includes(action) ? "default" : "pointer",
									}}
								>
									<CommentSVG />
								</Box>
							</IconButton>
						</ListItemSecondaryAction>
					)}
				</ListItem>
				{["add_tag", "update_tag"].includes(action) && (
					<ListItem style={{ padding: 0, paddingLeft: "72px" }}>
						<Box style={{ display: "flex" }}>
							{data?.history?.previous_object && (
								<Box
									style={{
										padding: "5px",
										background: hexToRGB(data?.history?.previous_object?.color_id, 0.1),
										borderRadius: "3px",
									}}
								>
									<Typography style={{ ...theme.typography.caption }}>
										{data?.history?.previous_object?.tag_name}
									</Typography>
								</Box>
							)}
							{data?.history?.previous_object?.tag_name && data?.history?.current_object?.tag_name && (
								<Box style={{ padding: "0 5px", display: "flex", alignItems: "center" }}>
									<ArrowForwardIcon fontSize={"small"} />
								</Box>
							)}
							{data?.history?.current_object && (
								<Box
									style={{
										padding: "5px",
										background: hexToRGB(data?.history?.current_object?.color_id, 0.1),
										borderRadius: "3px",
									}}
								>
									<Typography style={{ ...theme.typography.caption }}>
										{data?.history?.current_object?.tag_name}
									</Typography>
								</Box>
							)}
						</Box>
					</ListItem>
				)}

				{["update_tagged_date"].includes(action) && (
					<ListItem style={{ padding: 0, paddingLeft: "72px" }}>
						<Box style={{ display: "flex" }}>
							{data?.history?.previous_object && (
								<Box
									style={{
										padding: "5px",
										background: hexToRGB(theme.palette.text.primary, 0.1),
										borderRadius: "3px",
									}}
								>
									<Typography style={{ ...theme.typography.caption }}>
										{moment.utc(data?.history?.previous_object).local().format(TAGGED_DATE_FORMAT)}
									</Typography>
								</Box>
							)}
							{data?.history?.previous_object && data?.history?.current_object && (
								<Box style={{ padding: "0 5px", display: "flex", alignItems: "center" }}>
									<ArrowForwardIcon fontSize={"small"} />
								</Box>
							)}
							{data?.history?.current_object && (
								<Box
									style={{
										padding: "5px",
										background: hexToRGB(theme.palette.text.primary, 0.1),
										borderRadius: "3px",
									}}
								>
									<Typography style={{ ...theme.typography.caption }}>
										{moment.utc(data?.history?.current_object).local().format(TAGGED_DATE_FORMAT)}
									</Typography>
								</Box>
							)}
						</Box>
					</ListItem>
				)}
			</List>
		)
	}

	const generateListView = (item) => {
		switch (item?.action) {
			case "add_tag": {
				return templateForListView("add_tag", t("viewer.tabs.history.applied_tag", { name: item?.username }), item)
			}
			case "update_tag": {
				return templateForListView("update_tag", t("viewer.tabs.history.updated_tag", { name: item?.username }), item)
			}
			case "delete_tag": {
				return templateForListView("delete_tag", t("viewer.tabs.history.deleted_tag", { name: item?.username }), item)
			}
			case "add_comment": {
				return templateForListView("add_comment", t("viewer.tabs.history.added_comment", { name: item?.username }), item)
			}
			case "update_comment": {
				return templateForListView("update_comment", t("viewer.tabs.history.edit_comment", { name: item?.username }), item)
			}
			case "delete_comment": {
				return templateForListView("delete_comment", t("viewer.tabs.history.deleted_comment", { name: item?.username }), item)
			}
			case "update_tagged_date": {
				return templateForListView(
					"update_tagged_date",
					t("viewer.tabs.history.update_tagged_date", { name: item?.username }),
					item
				)
			}
			default: {
				return <></>
			}
		}
	}

	const stringAvatar = (name) => {
		return {
			style: {
				...theme?.typography?.avtarText,
				background: stringToColor(name),
				color: getContrast(stringToColor(name)),
				width: "32px",
				height: "32px",
			},
			children: name?.split(" ")[1] ? `${name?.split(" ")[0][0]}${name?.split(" ")[1][0]}` : name?.split(" ")[0][0],
		}
	}

	return (
		<Fragment>
			<Box className={classes.auditHistory}>
				<Grid>
					<Grid item xs={12}>
						{isAuditHistoryLoading ? (
							<>
								<Skeleton animation="wave" variant="rectangular" height="5vh" style={{ margin: "5px" }} />
								<Skeleton animation="wave" variant="rectangular" height="5vh" style={{ margin: "5px" }} />
								<Skeleton animation="wave" variant="rectangular" height="5vh" style={{ margin: "5px" }} />
								<Skeleton animation="wave" variant="rectangular" height="5vh" style={{ margin: "5px" }} />
								<Skeleton animation="wave" variant="rectangular" height="5vh" style={{ margin: "5px" }} />
							</>
						) : _.isEmpty(auditHistory) ? (
							<Typography style={{ ...theme.typography.body2 }}>{t("viewer.tabs.history.no_history")}</Typography>
						) : (
							<List dense={true}>
								{auditHistory.map((item) => {
									return generateListView(item)
								})}
							</List>
						)}
					</Grid>
				</Grid>
			</Box>
			<CommentDialog
				open={commentDialog}
				title={"Edited Comment"}
				onClose={() => {
					setCommentDialog(false)
				}}
			>
				<Box className={classes.commentDialog}>
					<Box style={{ display: "flex" }}>
						<Box style={{ padding: "10px" }}>
							<Box className={classes.commentContentTitle}>{t("viewer.tabs.history.previous_comment")}</Box>
							<Box>{commentDialogData?.history?.previous_object}</Box>
						</Box>
						<Box style={{ padding: "0 30px", display: "flex", alignItems: "center" }}>
							<TrendingFlatIcon />
						</Box>
						<Box style={{ padding: "10px" }}>
							<Box className={classes.commentContentTitle}>{t("viewer.tabs.history.edited_comment")}</Box>
							<Box>{commentDialogData?.history?.current_object}</Box>
						</Box>
					</Box>
				</Box>
			</CommentDialog>
		</Fragment>
	)
}

export default AuditHistory
